import { Component } from '@theme/component';

class SizeGuideDialog extends Component {
  /** @type {AbortController | undefined} */
  #keydownController;

  connectedCallback() {
    super.connectedCallback();
    // The Popover API promotes this element into the browser's top layer,
    // the same layer native <dialog> elements (like the quick-add modal)
    // render in. Top-layer elements always stack above regular content and
    // are immune to ancestor CSS transforms trapping their fixed positioning
    // - without it, opening this from inside the quick-add modal would
    // render behind that modal's own top-layer <dialog>.
    if (!this.hasAttribute('popover')) {
      this.setAttribute('popover', 'manual');
    }
    this.addEventListener('click', this.#handleBackdropClick);
    this.#keydownController = new AbortController();
    document.addEventListener('keydown', this.#handleKeydown, { signal: this.#keydownController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // The theme editor replaces this element's markup with fresh HTML
    // whenever a section setting changes, without going through close().
    // If the popover was left open at that moment, explicitly hide it here
    // so the browser's top layer doesn't keep rendering the now-detached
    // element as a stray fragment over the new preview.
    try {
      this.hidePopover();
    } catch {
      // Not currently shown as a popover - nothing to do.
    }
    this.classList.remove('sg-open');
    this.removeEventListener('click', this.#handleBackdropClick);
    this.#keydownController?.abort();
  }

  open() {
    try {
      this.showPopover();
    } catch {
      // Already open, or the browser doesn't support the Popover API - the
      // sg-open class below still makes the dialog visible as a fallback.
    }
    this.classList.add('sg-open');
    document.body.style.overflow = 'hidden';
    // The host dialog we were opened from (e.g. the quick-add modal) can
    // re-assert its own top-layer position shortly after this popover opens
    // - for instance while it finishes loading its fetched product content -
    // which would push it back above us. Re-showing once more on the next
    // frame makes this popover the most-recently-promoted top-layer element
    // again, winning that race so it stays on top.
    requestAnimationFrame(() => {
      if (!this.classList.contains('sg-open')) return;
      try {
        this.hidePopover();
        this.showPopover();
      } catch {
        // Nothing to do if the popover API isn't available here.
      }
    });
  }

  close() {
    this.classList.remove('sg-open');
    document.body.style.overflow = '';
    try {
      this.hidePopover();
    } catch {
      // Not currently shown as a popover - nothing to do.
    }
  }

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (event.target === this) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.classList.contains('sg-open')) this.close();
  };
}

if (!customElements.get('size-guide-dialog')) {
  customElements.define('size-guide-dialog', SizeGuideDialog);
}
